/**
 * Pure scene-diff helper. Given two `RenderScene`s (the previously
 * applied scene + the just-built one), classifies each node as
 * `insert` / `remove` / `update` and tags updates with a
 * `ChangeKind` so reconcilers can pick fast paths.
 *
 * Unchanged nodes are omitted — the diff is a minimal patch list.
 *
 * `ChangeKind` flags mirror the existing DOM optimization seams:
 *   - `contentChanged` → DOM full rebind path.
 *   - `layoutChanged` → DOM style-only `applyPlanResize` path.
 *   - `bindingChanged` → DOM swaps the cell plugin (rare).
 *   - `spanModeChanged` → DOM `applySpanMode` path.
 *
 * Reconcilers consume diffs in this fixed order: cells, then
 * rowOverlays, then legends. Within a category, entries follow
 * scene iteration order (insert order on the source `Map`).
 */

import type { Option } from "../core/option.ts";
import { ArtifactFreeze } from "@tento-core/artifact-freeze";

import type { ColumnKey } from "./layout-types.ts";
import {
  type CellNode,
  type LegendNode,
  type RenderScene,
  type RowOverlayNode,
  SceneKey,
} from "./scene.ts";
import type { RowLocalId } from "../ids/row-local-id.ts";
import { elementAt } from "../core/checked-index.ts";

/** Granularity flags for an `update` entry. */
export interface ChangeKind {
  readonly contentChanged: boolean;
  readonly layoutChanged: boolean;
  readonly bindingChanged: boolean;
  readonly spanModeChanged: boolean;
}

/**
 * In-artifact count-of-work for one diff (mirrors `SceneBuildCounters` /
 * `GeometrySnapshot.BuildCounters`). Fixtures assert amount-of-work — chiefly
 * `deepComparesSkipped`, the version fast-path's payoff — from the artifact.
 */
export interface SceneDiffCounters {
  /** Node pairs (present in both prev + next) the classifier ran on. */
  readonly compared: number;
  /**
   * Of the compared CELL pairs, ones whose deep content compare was skipped
   * because both nodes carried a stamp and their `valuesVersion` matched — the
   * versions (values + attr) decided the verdict, whether that verdict was
   * "unchanged" or an attribute-only content change.
   */
  readonly deepComparesSkipped: number;
  /** `insert` changes emitted across all categories. */
  readonly inserts: number;
  /** `update` changes emitted across all categories. */
  readonly updates: number;
  /** `remove` changes emitted across all categories. */
  readonly removes: number;
}

/** A single change against one scene node. */
export type SceneChange<TNode> =
  | { readonly tag: "insert"; readonly key: SceneKey; readonly next: TNode }
  | { readonly tag: "remove"; readonly key: SceneKey; readonly prev: TNode }
  | {
    readonly tag: "update";
    readonly key: SceneKey;
    readonly prev: TNode;
    readonly next: TNode;
    readonly what: ChangeKind;
  };

/** Per-category change lists. Reconcilers walk these in fixed order. */
export interface SceneDiff<K extends ColumnKey = ColumnKey> {
  readonly cells: readonly SceneChange<CellNode<K>>[];
  readonly rowOverlays: readonly SceneChange<RowOverlayNode>[];
  readonly legends: readonly SceneChange<LegendNode>[];
  /** In-artifact count-of-work for this diff. */
  readonly work: SceneDiffCounters;
}

/** Mutable count-of-work accumulator (frozen into `SceneDiffCounters`). */
interface DiffWork {
  compared: number;
  deepComparesSkipped: number;
  inserts: number;
  updates: number;
  removes: number;
}

/**
 * Classify the difference between two scenes.
 *
 * `prev = null` ⇒ every node in `next` is an `insert`. Subsequent
 * calls produce minimal patches.
 */
export function diffScene<K extends ColumnKey = ColumnKey>(
  prev: Option<RenderScene<K>>,
  next: RenderScene<K>,
): SceneDiff<K> {
  const work: DiffWork = {
    compared: 0,
    deepComparesSkipped: 0,
    inserts: 0,
    updates: 0,
    removes: 0,
  };
  // Two inputs-reuse builds over the same columns: a row whose input is the
  // same object holds the very same nodes in both scenes (an incremental build
  // carried them; a full one reused each by its inputs), which diff to nothing,
  // so only the rows whose input changed are compared.
  const scoped = prev != null ? changedKeys(prev, next) : null;
  const diff: SceneDiff<K> = {
    cells: diffCategory(
      prev?.cells ?? null,
      next.cells,
      (p, n) => diffCellNode(p, n, work),
      work,
      scoped?.cells ?? null,
    ),
    rowOverlays: diffCategory(
      prev?.rowOverlays ?? null,
      next.rowOverlays,
      diffRowOverlayNode,
      work,
      scoped?.rowOverlays ?? null,
    ),
    legends: diffCategory(
      prev?.legends ?? null,
      next.legends,
      diffLegendNode,
      work,
      scoped?.legends ?? null,
    ),
    work: {
      compared: work.compared,
      deepComparesSkipped: work.deepComparesSkipped,
      inserts: work.inserts,
      updates: work.updates,
      removes: work.removes,
    },
  };
  // Flowing-message immutability (engine-spec §3). No-op unless a test harness
  // enabled it.
  return ArtifactFreeze.deep(diff);
}

/** The scene keys of the rows whose input changed between two inputs-reuse scenes, by category; `null` when either is not one. */
function changedKeys<K extends ColumnKey>(
  prev: RenderScene<K>,
  next: RenderScene<K>,
): Option<{ readonly cells: readonly string[]; readonly rowOverlays: readonly string[]; readonly legends: readonly string[] }> {
  const was = prev.work?.inputs ?? null;
  const now = next.work?.inputs ?? null;
  if (was == null || now == null || !sameColumns(was.columns, now.columns)) return null;
  const rows: RowLocalId[] = [];
  for (const [id, input] of now.rows) if (was.rows.get(id) !== input) rows.push(id);
  for (const id of was.rows.keys()) if (!now.rows.has(id)) rows.push(id);
  const cells: string[] = [];
  const rowOverlays: string[] = [];
  const legends: string[] = [];
  for (const id of rows) {
    for (const column of now.columns) cells.push(SceneKey.toString({ kind: "cell", row: id, column }));
    const legend = SceneKey.toString({ kind: "legend", row: id });
    legends.push(legend);
    const plugins = new Set<string>();
    for (const scene of [prev, next]) {
      const node = scene.legends.get(legend);
      if (node != null) plugins.add(node.bindings.cell.pluginId);
    }
    for (const pluginId of plugins) rowOverlays.push(SceneKey.toString({ kind: "row-overlay", row: id, pluginId }));
  }
  return { cells, rowOverlays, legends };
}

function sameColumns(a: readonly ColumnKey[], b: readonly ColumnKey[]): boolean {
  return a === b || (a.length === b.length && a.every((column, i) => column === b[i]));
}

function diffCategory<TNode extends { readonly key: SceneKey }>(
  prev: Option<ReadonlyMap<string, TNode>>,
  next: ReadonlyMap<string, TNode>,
  classify: (prev: TNode, next: TNode) => Option<ChangeKind>,
  work: DiffWork,
  only: Option<readonly string[]>,
): readonly SceneChange<TNode>[] {
  const changes: SceneChange<TNode>[] = [];
  if (prev != null && only != null) {
    // Only these keys can differ: each is an insert, an update or a remove.
    for (const serialized of only) {
      const nextNode = next.get(serialized) ?? null;
      const prevNode = prev.get(serialized) ?? null;
      if (nextNode == null) {
        if (prevNode != null) {
          changes.push({ tag: "remove", key: prevNode.key, prev: prevNode });
          work.removes += 1;
        }
        continue;
      }
      if (prevNode == null) {
        changes.push({ tag: "insert", key: nextNode.key, next: nextNode });
        work.inserts += 1;
        continue;
      }
      work.compared += 1;
      const what = classify(prevNode, nextNode);
      if (what == null) continue;
      changes.push({ tag: "update", key: nextNode.key, prev: prevNode, next: nextNode, what });
      work.updates += 1;
    }
    return changes;
  }
  for (const [serialized, nextNode] of next) {
    const prevNode = prev?.get(serialized) ?? null;
    if (prevNode === null) {
      changes.push({ tag: "insert", key: nextNode.key, next: nextNode });
      work.inserts += 1;
      continue;
    }
    work.compared += 1;
    const what = classify(prevNode, nextNode);
    if (what == null) continue;
    changes.push({
      tag: "update",
      key: nextNode.key,
      prev: prevNode,
      next: nextNode,
      what,
    });
    work.updates += 1;
  }
  if (prev != null) {
    for (const [serialized, prevNode] of prev) {
      if (next.has(serialized)) continue;
      changes.push({ tag: "remove", key: prevNode.key, prev: prevNode });
      work.removes += 1;
    }
  }
  return changes;
}

function diffCellNode<K extends ColumnKey>(
  prev: CellNode<K>,
  next: CellNode<K>,
  work: DiffWork,
): Option<ChangeKind> {
  const bindingChanged =
    prev.bindings.cell.pluginId !== next.bindings.cell.pluginId ||
    prev.bindings.cell.pluginRole !== next.bindings.cell.pluginRole;
  // Row repositioning (rowIndex / columnIndex change) is a layout
  // concern — DOM needs to re-flow the cell even if its own state
  // hasn't changed. This arm stays live regardless of the stamps (geometry
  // is NOT covered by the output version — the S2p carve-out; see the
  // `CellNode.layout` coverage-boundary note in `scene.ts`).
  const layoutChanged = !cellLayoutEqual(prev.layout, next.layout) ||
    prev.rowIndex !== next.rowIndex ||
    prev.columnIndex !== next.columnIndex;
  const spanModeChanged = prev.plan.spanMode.type !== next.plan.spanMode.type;
  // Version verdict (LR6 / L5): when BOTH nodes carry a stamp, the stamped
  // `(valuesVersion, attrVersion)` pair decides and the deep content compare is
  // elided. A matching pair is the true "unchanged" skip; EITHER facet moving
  // is a positive content change (⇒ rebind downstream). This is exactly the
  // LR6 render-skip rule (`RenderSkip.decide`: skip ⟺ both recorded versions
  // equal the current ones) applied to the same `currentVersions` probe, so the
  // directive classifier can never claim `skip` for a cell the render cursor
  // would repaint. It used to fall through to the deep compare on a
  // `valuesVersion` move, which re-certified "unchanged" whenever a row's
  // version moved without THIS column's content changing — a batch
  // re-delivering identical cells, or a write to another column — and the cell
  // referee's layer-1 cross-check then contradicted every such skip (the
  // sticky `skip@7 … cursor would repaint` divergence on the weather row).
  // Only stamp-less nodes fall through to the deep compare. The
  // structural/layout arms above are unaffected.
  const ps = prev.stamp;
  const ns = next.stamp;
  let contentChanged: boolean;
  if (ps != null && ns != null) {
    contentChanged = ps.valuesVersion !== ns.valuesVersion ||
      ps.attrVersion !== ns.attrVersion ||
      // Clock transitions are not recorded in the row's ledger versions.
      !timeStateEqual(prev.plan.timeState, next.plan.timeState);
    work.deepComparesSkipped += 1;
  } else {
    contentChanged = prev.plan.descriptorType !== next.plan.descriptorType ||
      prev.plan.skipContent !== next.plan.skipContent ||
      !modeEqual(prev.plan.mode, next.plan.mode) ||
      !timeStateEqual(prev.plan.timeState, next.plan.timeState) ||
      prev.plan.stacking?.subColumns.length !==
        next.plan.stacking?.subColumns.length ||
      !cellContentEqual(prev.content, next.content);
  }
  if (
    !contentChanged && !layoutChanged && !bindingChanged && !spanModeChanged
  ) {
    return null;
  }
  return { contentChanged, layoutChanged, bindingChanged, spanModeChanged };
}

function cellContentEqual(
  a: Option<CellNode["content"]>,
  b: Option<CellNode["content"]>,
): boolean {
  if (a === b) return true;
  if (a == null || b == null) return false;
  if (a.kind !== b.kind) return false;
  if (a.kind === "none" || b.kind === "none") return true;
  // Both are { kind: "value"; entry: ... }
  const ae = a.entry;
  const be = b.entry;
  if (ae == null) return be == null;
  if (be == null) return false;
  if ((ae.id ?? null) !== (be.id ?? null)) return false;
  return ae.value === be.value;
}

function diffRowOverlayNode(
  prev: RowOverlayNode,
  next: RowOverlayNode,
): Option<ChangeKind> {
  const bindingChanged =
    prev.bindings.cell.pluginId !== next.bindings.cell.pluginId ||
    prev.bindings.cell.pluginRole !== next.bindings.cell.pluginRole;
  const contentChanged = !intentListEqual(prev.intents, next.intents);
  const layoutChanged = prev.rowIndex !== next.rowIndex;
  if (!contentChanged && !bindingChanged && !layoutChanged) return null;
  return {
    contentChanged,
    layoutChanged,
    bindingChanged,
    spanModeChanged: false,
  };
}

function diffLegendNode(
  prev: LegendNode,
  next: LegendNode,
): Option<ChangeKind> {
  const bindingChanged =
    prev.bindings.cell.pluginId !== next.bindings.cell.pluginId ||
    prev.bindings.cell.pluginRole !== next.bindings.cell.pluginRole;
  const contentChanged = prev.label !== next.label;
  const layoutChanged = prev.rowIndex !== next.rowIndex;
  if (!contentChanged && !bindingChanged && !layoutChanged) return null;
  return {
    contentChanged,
    layoutChanged,
    bindingChanged,
    spanModeChanged: false,
  };
}

function cellLayoutEqual(
  a: Option<CellNode["layout"]>,
  b: Option<CellNode["layout"]>,
): boolean {
  if (a === b) return true;
  if (a == null || b == null) return false;
  if (a.cellHeightPx !== b.cellHeightPx) return false;
  if (a.cellHeightWithChildrenPx !== b.cellHeightWithChildrenPx) return false;
  if (a.offsetPx !== b.offsetPx) return false;
  const stackEqual = a.stackingInfo == null
    ? b.stackingInfo == null
    : b.stackingInfo != null &&
      a.stackingInfo.cellsPerColumn === b.stackingInfo.cellsPerColumn &&
      a.stackingInfo.individualCellHeight ===
        b.stackingInfo.individualCellHeight &&
      a.stackingInfo.visibleCellCount === b.stackingInfo.visibleCellCount &&
      a.stackingInfo.seeMoreHeight === b.stackingInfo.seeMoreHeight;
  return stackEqual;
}

function modeEqual(
  a: CellNode["plan"]["mode"],
  b: CellNode["plan"]["mode"],
): boolean {
  if (a.type !== b.type) return false;
  if (a.type === "spanned" && b.type === "spanned") {
    return a.isStatic === b.isStatic;
  }
  if (a.type === "span-stacked" && b.type === "span-stacked") {
    return a.spanTimescale === b.spanTimescale && a.spanCount === b.spanCount;
  }
  return true;
}

function timeStateEqual(
  a: CellNode["plan"]["timeState"],
  b: CellNode["plan"]["timeState"],
): boolean {
  return a.inThePast === b.inThePast &&
    a.isCurrent === b.isCurrent &&
    a.isFuture === b.isFuture;
}

function intentListEqual(
  a: RowOverlayNode["intents"],
  b: RowOverlayNode["intents"],
): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const ai = elementAt(a, i, "scene-diff: a");
    const bi = elementAt(b, i, "scene-diff: b");
    if (ai.id !== bi.id) return false;
    if (ai.pluginId !== bi.pluginId) return false;
    if (!ai.range.start.equals(bi.range.start)) return false;
    if (!ai.range.end.equals(bi.range.end)) return false;
    if ((ai.align ?? null) !== (bi.align ?? null)) return false;
    if ((ai.fillWidth ?? false) !== (bi.fillWidth ?? false)) return false;
    if ((ai.isOverflow ?? false) !== (bi.isOverflow ?? false)) return false;
  }
  return true;
}
